import { Order, Reservation, OfficeConfig, ContractorConfig } from "@types";
import React from "react";
import PageNav from "components/PageNav/PageNav";
import ScheduleDeliveryTabs from "./ScheduleDeliveryTabs";
import {
  DeliveryFlowStep,
  DELIVERY_FLOW_STEP,
  getDefaultDeliveryFlowStep,
  getDeliveryFlowTabs,
  isDeliveryFlowStepAccessible,
} from "./deliveryFlow";
import { isClientInformationComplete } from "./ScheduleDeliveryTabs/ClientInformationForm/validationSchema";

interface ScheduleDeliveryProps {
  order: Order;
  hasReservation: boolean;
  reservation?: Reservation | null;
  officeConfig?: OfficeConfig | null;
  contractorConfig?: ContractorConfig | null;
  onReservationExpire?: () => void;
  onReservationUpdate?: (reservation: Reservation) => void;
  onCancelReservation?: () => Promise<void>;
  isCancellingReservation?: boolean;
  initialStep?: DeliveryFlowStep;
  initialStepKey?: string;
}

const ScheduleDelivery = ({
  order,
  hasReservation,
  reservation,
  officeConfig,
  contractorConfig,
  onReservationExpire,
  onReservationUpdate,
  onCancelReservation,
  isCancellingReservation = false,
  initialStep,
  initialStepKey,
}: ScheduleDeliveryProps) => {
  const [activeStep, setActiveStep] = React.useState<DeliveryFlowStep>(() =>
    getDefaultDeliveryFlowStep(hasReservation),
  );
  const [isCoordonneesComplete, setIsCoordonneesComplete] = React.useState(() =>
    isClientInformationComplete(order.client),
  );
  const lastAppliedInitialStepKey = React.useRef<string | undefined>(undefined);

  const tabs = React.useMemo(
    () =>
      getDeliveryFlowTabs({
        hasReservation,
        isCoordonneesComplete,
      }),
    [hasReservation, isCoordonneesComplete],
  );

  React.useEffect(() => {
    setIsCoordonneesComplete(isClientInformationComplete(order.client));
  }, [order.client]);

  React.useEffect(() => {
    if (!initialStep || !initialStepKey) {
      return;
    }

    if (lastAppliedInitialStepKey.current === initialStepKey) {
      return;
    }

    lastAppliedInitialStepKey.current = initialStepKey;
    setActiveStep(
      isDeliveryFlowStepAccessible(initialStep, tabs)
        ? initialStep
        : DELIVERY_FLOW_STEP.coordonnees,
    );
  }, [initialStep, initialStepKey, tabs]);

  React.useEffect(() => {
    if (tabs.some((tab) => tab.id === activeStep && !tab.disabled)) {
      return;
    }

    setActiveStep(DELIVERY_FLOW_STEP.coordonnees);
  }, [activeStep, tabs]);

  return (
    <div className="schedule-delivery">
      <PageNav
        tabs={tabs}
        activeTab={activeStep}
        onTabChange={(tab) => setActiveStep(tab.id as DeliveryFlowStep)}
      />
      <ScheduleDeliveryTabs
        order={order}
        tabs={tabs}
        activeStep={activeStep}
        onStepChange={setActiveStep}
        isCoordonneesComplete={isCoordonneesComplete}
        setIsCoordonneesComplete={setIsCoordonneesComplete}
        hasReservation={hasReservation}
        reservation={reservation}
        officeConfig={officeConfig}
        contractorConfig={contractorConfig}
        onReservationExpire={onReservationExpire}
        onReservationUpdate={onReservationUpdate}
        onCancelReservation={onCancelReservation}
        isCancellingReservation={isCancellingReservation}
      />
    </div>
  );
};

export default ScheduleDelivery;
